import { PaymentMethod } from '@stripe/stripe-js';
import {
  ReactivationCampaignType,
  ReactivationDetails,
  ReactivationScenario,
  ReactivationSubscriptionDetails,
} from '../reactivation.types';
import { getCampaignFromSession } from 'src/util/campaign.util';

/**
 * Toggle this to true to force mock data locally before the API is ready.
 * Can also be toggled via query params:
 *   - ?mock=true / ?mock=false
 *   - ?mockCampaign=FREE_MONTH / ?mockCampaign=DISCOUNT_ROLLOVER
 *   - ?mockScenario=HAS_SUBSCRIPTION_NO_PAYMENT
 *   - ?mockPayment=false
 */
export const USE_MOCK_REACTIVATION = true;

export const DEFAULT_MOCK_SCENARIO: ReactivationScenario =
  ReactivationScenario.HAS_SUBSCRIPTION_HAS_PAYMENT;

export const MOCK_PAYMENT_METHOD = {
  id: 'pm_1NmockCardPaymentMethod0001',
  object: 'payment_method',
  type: 'card',
  created: 1700000000,
  livemode: false,
  card: {
    brand: 'visa',
    last4: '4444',
    exp_month: 10,
    exp_year: 2027,
    country: 'AU',
    funding: 'credit',
    checks: null,
    three_d_secure_usage: null,
    wallet: null,
  },
  billing_details: {
    name: 'Sample User',
    email: 'user@example.com',
    phone: null,
    address: {
      city: 'Sydney',
      country: 'AU',
      line1: '123 Main St',
      line2: null,
      postal_code: '2000',
      state: 'NSW',
    },
  },
} as unknown as PaymentMethod;

export const MOCK_SUBSCRIPTION: ReactivationSubscriptionDetails = {
  planId: 'Standard Plan - 3 Months',
  priceId: 'price_standard_3m_aud',
  planName: 'Standard Plan - 3 Months',
  interval: 'month',
  intervalCount: 3,
  pricePerDay: '1.96',
  totalPrice: '177.00',
  nextBillingDate: Math.floor(Date.now() / 1000) + 90 * 24 * 60 * 60,
  invoiceDiscount: null,
  currencyOptions: {
    aud: {
      custom_unit_amount: null,
      tax_behavior: null,
      unit_amount: 17700,
      unit_amount_decimal: '17700',
    },
    nzd: {
      custom_unit_amount: null,
      tax_behavior: null,
      unit_amount: 19900,
      unit_amount_decimal: '19900',
    },
  },
  challengePackage: null,
  nutritionCoachingAddon: null,
};

/** Mock subscription for FREE_MONTH campaign ($0 now, 1 month free, renews after 1 month) */
export const MOCK_FREE_MONTH_SUBSCRIPTION: ReactivationSubscriptionDetails = {
  ...MOCK_SUBSCRIPTION,
  totalPrice: '0',
  originalTotalPrice: 177,
  invoiceDiscount: null,
  nextBillingDate: Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60,
};

/** Mock response for FREE_MONTH campaign */
export const MOCK_FREE_MONTH_RESPONSE: ReactivationDetails = {
  scenario: ReactivationScenario.HAS_SUBSCRIPTION_HAS_PAYMENT,
  subscription: MOCK_FREE_MONTH_SUBSCRIPTION,
  paymentMethod: MOCK_PAYMENT_METHOD,
  shouldOnlyReactivateSubscription: false,
  campaignType: ReactivationCampaignType.FREE_MONTH,
};

/** Mock subscription for DISCOUNT_ROLLOVER campaign (50% off for 3 months, full price after) */
export const MOCK_DISCOUNT_ROLLOVER_SUBSCRIPTION: ReactivationSubscriptionDetails =
  {
    ...MOCK_SUBSCRIPTION,
    totalPrice: '88.50',
    originalTotalPrice: 177,
    pricePerDay: '0.98',
    invoiceDiscount: {
      id: 'di_mock50Percent',
      percent_off: 50,
    } as any,
    currencyOptions: {
      aud: {
        custom_unit_amount: null,
        tax_behavior: null,
        unit_amount: 8850,
        unit_amount_decimal: '8850',
      },
      nzd: {
        custom_unit_amount: null,
        tax_behavior: null,
        unit_amount: 9950,
        unit_amount_decimal: '9950',
      },
    },
  };

/** Mock response for DISCOUNT_ROLLOVER campaign */
export const MOCK_DISCOUNT_ROLLOVER_RESPONSE: ReactivationDetails = {
  scenario: ReactivationScenario.HAS_SUBSCRIPTION_HAS_PAYMENT,
  subscription: MOCK_DISCOUNT_ROLLOVER_SUBSCRIPTION,
  paymentMethod: MOCK_PAYMENT_METHOD,
  shouldOnlyReactivateSubscription: false,
  campaignType: ReactivationCampaignType.DISCOUNT_ROLLOVER,
};

export const MOCK_REACTIVATION_RESPONSES: Record<
  ReactivationScenario,
  ReactivationDetails
> = {
  [ReactivationScenario.HAS_SUBSCRIPTION_HAS_PAYMENT]: {
    scenario: ReactivationScenario.HAS_SUBSCRIPTION_HAS_PAYMENT,
    subscription: MOCK_SUBSCRIPTION,
    paymentMethod: MOCK_PAYMENT_METHOD,
    shouldOnlyReactivateSubscription: false,
    campaignType: null,
  },
  [ReactivationScenario.HAS_SUBSCRIPTION_NO_PAYMENT]: {
    scenario: ReactivationScenario.HAS_SUBSCRIPTION_NO_PAYMENT,
    subscription: MOCK_SUBSCRIPTION,
    paymentMethod: null,
    shouldOnlyReactivateSubscription: false,
    campaignType: null,
  },
  [ReactivationScenario.NO_SUBSCRIPTION_NO_PAYMENT]: {
    scenario: ReactivationScenario.NO_SUBSCRIPTION_NO_PAYMENT,
    subscription: null,
    paymentMethod: null,
    shouldOnlyReactivateSubscription: false,
    campaignType: null,
  },
};

export const getActiveMockScenario = (): ReactivationScenario => {
  try {
    const params = new URLSearchParams(window.location.search);
    const scenarioParam = params.get('mockScenario');
    if (
      scenarioParam &&
      Object.values(ReactivationScenario).includes(
        scenarioParam as ReactivationScenario
      )
    ) {
      return scenarioParam as ReactivationScenario;
    }
  } catch {
    // fallback
  }
  return DEFAULT_MOCK_SCENARIO;
};

/**
 * Determine the active mock response based on:
 * 1. URL params: ?mockCampaign=FREE_MONTH or ?mockCampaign=DISCOUNT_ROLLOVER
 * 2. URL params / session: ?campaign=FREE_MONTH_REACTIVATION or ?campaign=DISCOUNT_ROLLOVER_REACTIVATION
 * 3. URL params: ?mockScenario=...
 * 4. URL params: ?mockPayment=false (to simulate user without card on file)
 */
export const getActiveMockDetails = (
  campaignCode?: string | null
): ReactivationDetails => {
  let params: URLSearchParams | null = null;

  try {
    if (typeof window !== 'undefined') {
      params = new URLSearchParams(window.location.search);
    }
  } catch {
    // ignore
  }

  const mockCampaignParam = params?.get('mockCampaign')?.toUpperCase();
  const mockScenarioParam = params?.get('mockScenario');
  const sessionCampaign = getCampaignFromSession()?.toUpperCase();
  const activeCode = (
    campaignCode ||
    sessionCampaign ||
    params?.get('campaign') ||
    ''
  ).toUpperCase();

  const hasPayment =
    params?.get('mockPayment') !== 'false' &&
    !mockScenarioParam?.includes('NO_PAYMENT');
  const paymentMethod = hasPayment ? MOCK_PAYMENT_METHOD : null;
  const scenarioForSub = (hasSub: boolean) => {
    if (!hasSub) return ReactivationScenario.NO_SUBSCRIPTION_NO_PAYMENT;
    return hasPayment
      ? ReactivationScenario.HAS_SUBSCRIPTION_HAS_PAYMENT
      : ReactivationScenario.HAS_SUBSCRIPTION_NO_PAYMENT;
  };

  // 1. FREE_MONTH campaign mock
  if (
    mockCampaignParam === 'FREE_MONTH' ||
    mockCampaignParam === 'FREE_MONTH_REACTIVATION' ||
    mockScenarioParam?.toUpperCase().includes('FREE_MONTH') ||
    activeCode === 'FREE_MONTH_REACTIVATION'
  ) {
    return {
      scenario: scenarioForSub(true),
      subscription: MOCK_FREE_MONTH_SUBSCRIPTION,
      paymentMethod,
      shouldOnlyReactivateSubscription: false,
      campaignType: ReactivationCampaignType.FREE_MONTH,
    };
  }

  // 2. DISCOUNT_ROLLOVER campaign mock
  if (
    mockCampaignParam === 'DISCOUNT_ROLLOVER' ||
    mockCampaignParam === 'DISCOUNT_ROLLOVER_REACTIVATION' ||
    mockScenarioParam?.toUpperCase().includes('DISCOUNT_ROLLOVER') ||
    activeCode === 'DISCOUNT_ROLLOVER_REACTIVATION'
  ) {
    return {
      scenario: scenarioForSub(true),
      subscription: MOCK_DISCOUNT_ROLLOVER_SUBSCRIPTION,
      paymentMethod,
      shouldOnlyReactivateSubscription: false,
      campaignType: ReactivationCampaignType.DISCOUNT_ROLLOVER,
    };
  }

  // 3. Standard scenario mock
  const activeScenario = getActiveMockScenario();
  const baseResponse = MOCK_REACTIVATION_RESPONSES[activeScenario];
  return {
    ...baseResponse,
    paymentMethod: hasPayment ? baseResponse.paymentMethod : null,
    scenario: scenarioForSub(Boolean(baseResponse.subscription)),
  };
};

export const shouldUseMockData = (): boolean => {
  try {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mock') === 'false') {
        return false;
      }
      if (
        params.get('mock') === 'true' ||
        params.has('mockCampaign') ||
        params.has('mockScenario') ||
        params.has('mockPayment')
      ) {
        return true;
      }
      const local = localStorage.getItem('USE_MOCK_REACTIVATION');
      if (local === 'true') return true;
      if (local === 'false') return false;
    }
  } catch {
    // fallback
  }
  return USE_MOCK_REACTIVATION;
};

export const fetchMockReactivationDetails = async (
  campaignCode?: string | null
): Promise<ReactivationDetails> => {
  // Simulate brief network delay
  await new Promise(resolve => setTimeout(resolve, 300));
  return getActiveMockDetails(campaignCode);
};
